// CommunitiesPage.tsx — public Communities directory over the A4 shell.
import { useMemo, useState } from 'react';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@/ui';
import { media } from '@/theme/media';
import { GuestBanner } from '@/components/GuestBanner';
import { useCommunities, useSession, useRequireAuth } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { CommunityTabs } from './CommunityTabs';
import { CommunitySearch } from './CommunitySearch';
import { CommunityHero } from './CommunityHero';
import { PopularCommunityCard } from './PopularCommunityCard';
import { RecommendedCard } from './RecommendedCard';
import { InvitesList } from './InvitesList';
import { CreateCommunityDialog } from './CreateCommunityDialog';
import { TrendingCommunitiesPanel } from './panels/TrendingCommunitiesPanel';
import { MyCommunitiesPanel } from './panels/MyCommunitiesPanel';
import { CommunityInvitesPanel } from './panels/CommunityInvitesPanel';
import { searchCommunities, popularCommunities, recommendedCommunities } from './filterCommunities';
import type { DirTab } from './meta';

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;
const Titles = styled.div``;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;
const Title = styled.h1`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Subtitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const BannerSlot = styled.div`
  margin: 12px 0;
`;
const SectionTitle = styled.h2`
  margin: 18px 0 10px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;
const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

export function CommunitiesPage() {
  const { isAuthenticated } = useSession();
  const { communities, joined, invites } = useCommunities();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<DirTab>('discover');
  const [searchText, setSearchText] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const searched = useMemo(
    () => searchCommunities(communities, searchText),
    [communities, searchText],
  );
  const popular = useMemo(() => popularCommunities(searched), [searched]);
  const recs = useMemo(() => recommendedCommunities(searched), [searched]);
  const followed = useMemo(() => searchCommunities(joined, searchText), [joined, searchText]);

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="communities">
        <RailPanel id="trending" label="Trending communities">
          <TrendingCommunitiesPanel />
        </RailPanel>
        <RailPanel id="joined" label="My communities">
          <MyCommunitiesPanel />
        </RailPanel>
        <RailPanel id="invites" label="Invitations">
          <CommunityInvitesPanel />
        </RailPanel>
      </RailGroup>
    ),
    [],
  );
  useRightRail(rightRail);

  function openCreate() {
    if (!requireAuth('create a community')) return;
    setCreateOpen(true);
  }

  return (
    <section>
      <Header>
        <Titles>
          <Title>Communities</Title>
          <Subtitle>Connect, share, and grow with like-minded people.</Subtitle>
        </Titles>
        <HeaderActions>
          <NetworkLinks destination="communities" />
          <Button variant="primary" onClick={openCreate}>
            Create Community
          </Button>
        </HeaderActions>
      </Header>

      {!isAuthenticated && (
        <BannerSlot>
          <GuestBanner />
        </BannerSlot>
      )}

      <CommunitySearch value={searchText} onChange={setSearchText} />
      <CommunityTabs active={activeTab} onChange={setActiveTab} inviteCount={invites.length} />

      {activeTab === 'discover' && (
        <>
          <CommunityHero
            onJoin={() => setActiveTab('discover')}
            onViewMine={() => setActiveTab('following')}
          />
          <SectionTitle>Popular Communities</SectionTitle>
          {popular.length === 0 ? (
            // Blames the search only when one was typed. On prod this list is empty
            // for the ordinary reason — nobody has made a community yet — and a
            // member who typed nothing was being told their search failed.
            <Empty>
              {searchText.trim() ? 'No communities match your search.' : 'No communities yet.'}
            </Empty>
          ) : (
            <Grid>
              {popular.map((c) => (
                <PopularCommunityCard key={c.id} community={c} />
              ))}
            </Grid>
          )}
          {recs.length > 0 && (
            <>
              <SectionTitle>Recommended for You</SectionTitle>
              <Grid>
                {recs.map((c) => (
                  <RecommendedCard key={c.id} community={c} />
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {activeTab === 'following' &&
        (followed.length === 0 ? (
          <Empty>You haven&apos;t joined any communities yet.</Empty>
        ) : (
          <Grid>
            {followed.map((c) => (
              <PopularCommunityCard key={c.id} community={c} />
            ))}
          </Grid>
        ))}

      {activeTab === 'invitations' && <InvitesList invites={invites} />}

      <CreateCommunityDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(c) => {
          setCreateOpen(false);
          navigate(`/communities/${c.id}`);
        }}
      />
    </section>
  );
}
